import React, {createContext, useContext} from 'react';
import {Link, matchPath, Outlet, useLocation, useParams} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from "date-fns";
import { auth } from '../../firebase-config';
import { EpisodeModel, SeasonModel, SeriesModel } from "../../models";
import { useTvDetails } from "../../layouts/TvLayout";
import { setBackgroundImage } from '../../helpers/helpers';

import InfoList from "../../components/InfoList";
import Loader from '../../components/Loader';
import PersonList from "../../components/PersonList";
import Ratings from '../../components/Ratings';

const formatDate = (date: string) => {
    if (!date) return 'No Air Date';
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true } as Intl.DateTimeFormatOptions;
    return new Date(date).toLocaleDateString(undefined, options).replace('at', '').replace('am', 'AM').replace('pm', 'PM');
}

const formatTime = (time: number | undefined) => {
    if (!time) return;
    const hours = Math.floor(time / 60);
    const minutes = time % 60;
    return `${hours > 0 ? `${hours}h ` : ''}${minutes}min`;
}

const findEpisodeBadge = (episode: EpisodeModel, season: SeasonModel, tvDetails: SeriesModel) => {
    if (!episode) return null;

    if (episode.episode_number === 1) {
        return episode.season_number === 1 ? 'Series Premiere' : 'Season Premiere';
    }

    if (episode.episode_number === season.episodes.length) {
        const lastSeason = tvDetails.seasons[tvDetails.seasons.length - 1];
        return season.id === lastSeason.id && tvDetails.status === 'Ended' ? 'Series Finale' : 'Season Finale';
    }

    return null;
}

const getSeasonDetails = async (seriesId: number | undefined, seasonNumber: string | undefined): Promise<SeasonModel> => {
    const apiKey = process.env.REACT_APP_TMDB_API_KEY;
    const response = await fetch(`https://api.themoviedb.org/3/tv/${seriesId}/season/${seasonNumber}?append_to_response=videos,aggregate_credits`, {
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${apiKey}`,
        }
    });

    return await response.json();
}

export const SeasonContext = createContext<SeasonModel | null>(null);
export const useSeasonDetails = () => useContext(SeasonContext);

const Season = () => {
    const { season_number } = useParams<{ season_number: string }>();
    const tvDetails = useTvDetails();

    const location = useLocation();
    const isEpisodePage = matchPath('/tv/:id/season/:season_number/episode/:episode_number', location.pathname);

    const { data: seasonDetails, isLoading, error } = useQuery<SeasonModel>({
        queryKey: ['season-details', tvDetails?.id, season_number],
        queryFn: () => getSeasonDetails(tvDetails?.id, season_number),
        enabled: !!tvDetails && !!season_number,
        staleTime: 5 * 60 * 1000,
    });

    if (isLoading || !tvDetails || !seasonDetails) return <Loader fullscreen message="Loading..." />;
    if (error || !season_number) return <div>Error loading Season details.</div>;

    seasonDetails.media_type = 'season';
    setBackgroundImage(tvDetails.backdrop_path);

    const seasonTitle = tvDetails.seasons?.find((season: SeasonModel) => season.season_number === parseInt(season_number))?.name || `Season ${season_number}`;
    const totalRuntime = seasonDetails.episodes.reduce((total: number, episode: EpisodeModel) => total + (episode.runtime || 0), 0);
    const infoList = [
        { label: 'Premiered', value: seasonDetails.air_date ? format(new Date(seasonDetails.air_date), 'd MMMM yyyy') : null },
        { label: 'Genres', value: tvDetails.genres?.map((genre: { name: string }) => genre.name).join(', ') },
        { label: 'Total Runtime', value: totalRuntime > 0 ? formatTime(totalRuntime) : null },
    ]

    return (
      <>
          <SeasonContext.Provider value={seasonDetails}>
              {isEpisodePage ? (
                  <Outlet />
              ) : (
                  <>
                      <div className="details-summary-container">
                          <div className="details-poster">
                              <img src={`https://image.tmdb.org/t/p/w500${seasonDetails.poster_path ? seasonDetails.poster_path : tvDetails.poster_path}`} alt={`Season ${season_number}`} />
                              {auth.currentUser && (
                                <button className="history-button">Add to History</button>
                              )}
                          </div>

                          <div className="details-summary-content">
                              <div className="content-children">
                                <div className="title-container">
                                    <Link to={`/tv/${tvDetails.id}`} className="series-link">{tvDetails.name}</Link>
                                    <h1 className="details-title">{seasonTitle}</h1>

                                    {tvDetails.seasons.length > 1 && (
                                        <div className="season-picker">
                                            <div>Season | </div>
                                            {tvDetails.seasons.slice().reverse().map((season: SeasonModel) => (
                                                <div key={season.id}>
                                                    <Link to={`/tv/${tvDetails.id}/season/${season.season_number}`} className={`season-link ${season.season_number === parseInt(season_number) ? 'active' : ''}`}>
                                                        {season.season_number === 0 ? 'Specials' : `${season.season_number}`}
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                              </div>

                                <p className="details-overview">{seasonDetails.overview || tvDetails.overview}</p>
                          </div>
                      </div>

                      <div className="details-summary-container">
                          <div className="details-summary-content">
                              <InfoList items={infoList} />
                          </div>
                      </div>

                      {seasonDetails?.aggregate_credits?.cast &&
                          <div className="details-person-container">
                              <PersonList people={seasonDetails.aggregate_credits.cast} />
                          </div>
                      }

                      <div className="season-episodes-container">
                          <div className="season-episodes">
                              {seasonDetails.episodes.length === 0 && <></>}
                              {seasonDetails.episodes.length > 0 && <h2>{seasonDetails.episodes.length} Episodes</h2>}
                              {seasonDetails.episodes.map((episode: EpisodeModel) => (
                                  <div key={episode.id} className="episode">
                                      <img className='episode-image' src={`https://image.tmdb.org/t/p/w500${episode.still_path ? episode.still_path : tvDetails.backdrop_path}`} alt={episode.name} />
                                      <div className="episode-details">
                                          <span className="badge">{findEpisodeBadge(episode, seasonDetails, tvDetails)}</span>
                                          <Link to={`episode/${episode.episode_number}`} className="episode-title">{season_number}x{episode.episode_number < 10 ? `0${episode.episode_number}` : episode.episode_number} {episode.name}</Link>
                                          <p>{formatDate(episode.air_date)}
                                              {episode.runtime &&
                                                  <span className='runtime'> - {formatTime(episode.runtime)}</span>
                                              }
                                          </p>

                                          <Ratings details={episode} link={`tv/${tvDetails.id}/season/${season_number}/episode/${episode.episode_number}`} />

                                          <p>{episode.overview}</p>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      </div>
                  </>
              )}
          </SeasonContext.Provider>
      </>
    );
};

export default Season;
